<template>
  <div>
    <LoadingProductsListPage v-if="isFetching && !facets.size" class="[ container mx-auto ]" />
    <div v-else>
      <section v-if="showListingView" class="ProductListing__inner">
        <div class="px-5 lg:px-0 flex flex-col space-y-10">
          <slot name="filter-banner" />
          <div class="hidden lg:block px-6 lg:px-0 ProductListing__filters">
            <h2 class="text-primary-A900 text-xl font-display font-bold antialiased relative">
              {{ $t('filterBy') }}
            </h2>
            <div data-cy-region="filters" class="Facets mt-5">
              <FacetDropdown
                v-for="[code, facet] in appliedFacet"
                :key="code"
                :value="facet.value"
                :code="facet.code"
                :options="facet.options"
                :label="facet.label"
                :type="facet.type"
                :is-localized="true"
                @input="$event => updateFacetWithSelectedValue($event, facet)"
              />
            </div>
          </div>
        </div>

        <div class="ProductListing__products">
          <slot />
          <div class="px-6 lg:px-0 flex flex-col lg:flex-row lg:items-center overflow-hidden lg:overflow-visible">
            <div class="mt-5 lg:mt-0 flex items-center justify-between w-full">
              <h3 class="text-primary-A900 font-bold">
                {{ $t('products', { totalCount }) }}
              </h3>
              <button
                class="lg:hidden flex items-center justify-center text-sm text-primary-500 underline font-medium"
                type="button"
                :aria-label="$t('filterProducts')"
                @click="isFiltering = true"
              >
                <svg-icon name="filter" width="30" height="30" />
              </button>
              <SortProductsDropdown v-model="selectedOption" class="hidden lg:inline-block shadow-custom-1" />
            </div>
          </div>
          <div v-if="displayFacetTags" class="mt-6 w-full py-2 lg:py-3 px-5 lg:px-0 [ flex gap-2 flex-wrap ]">
            <FacetsTagsList
              v-for="[code, facet] in appliedFacet"
              :key="code"
              :facet="facet"
              :label="facet.label"
              @remove-selected-facet="
                ({ facet, value }) => {
                  currentPage = 1;
                  removeItem({
                    code: facet.code,
                    value,
                  });
                }
              "
            />
            <button
              v-if="displayFacetTags"
              class="text-primary-700 underline text-sm"
              @click="
                () => {
                  currentPage = 1;
                  clearAll();
                }
              "
            >
              {{ $t('clearAll') }}
            </button>
          </div>
          <LoadingProductsList v-if="isFetching" />

          <template v-else-if="products">
            <div
              id="product-listing"
              data-cy-region="product-listing"
              class="mt-5 ProductsGrid justify-center px-6 lg:px-0"
            >
              <ProductCard v-for="product in products" :key="product.id" :product="product" landscape />
            </div>
          </template>
          <LoadingProductsList v-if="isFetching" />
          <slot name="empty">
            <EmptyMessage
              v-if="!isFetching && totalCount === 0"
              href="/"
              icon="delivery-active"
              :title="$t('noResultsFound')"
              button-icon="cart-active-white"
              class="bg-white py-8"
            >
              <template #title>
                <p class="mt-5 text-xl text-primary-A900 font-bold max-w-87">
                  {{ $t('noResultsFound') }}
                </p>
              </template>

              <template #message>
                <p class="mt-5 text-base text-secondary-700 max-w-100 text-center">
                  {{ $t('noResultsFoundDescription') }}
                </p>
              </template>

              <template #icon>
                <svg-icon name="empty-products" width="212" height="145" class="w-50 h-32 lg:w-87 lg:h-[232px]" />
              </template>
            </EmptyMessage>
          </slot>

          <PageInfo
            :is-fetching="isFetching"
            :total-count="totalCount"
            :current-count="products.length"
            class="mt-5 lg:mt-10"
            top-el-id="product-listing"
            @next="nextPage"
          />
        </div>

        <FacetDialog
          v-model:is-open="isFiltering"
          :value="appliedFacet"
          :with-sorting="type !== 'search'"
          @sort="(e: typeof sortBy) => (sortBy = e)"
          @apply="e => updateFacetDialogWithSelectedValue(e)"
        />
      </section>
      <section v-else>
        <slot name="emptyReplacement" />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AggregationOption, ProductListingType } from '@robustastudio/e-commerce/common';
import { isAggregationOption, useAppFacets, type Facets, isValidPriceValue } from '~/composables/facets/facets';
import { merge, omit } from 'lodash-es';
import { SortEnum } from '~/graphql-types.gen';
import {
  isPriceProps,
  isSelectProps,
  isToggleProps,
  type AllFacets,
  type IFacet,
  type ISelectFacet,
} from '~/types/facets';

const defaultSortOption = {};

const { selectedOption: sortBy } = useSortBy();

const { t: $t } = useI18n({
  useScope: 'local',
});

function enabledFacet(facet: IFacet, included?: string[]): boolean {
  // If no preferable facets, then return them all
  if (!(included && included?.length)) {
    return !!facet.code;
  }

  return included.includes(facet.code);
}

interface Filter {
  in: string[];
}

interface DefinedFilter {
  [key: string]: Filter;
}

const props = withDefaults(
  defineProps<{
    type?: ProductListingType;
    value?: Facets;
    search?: string;
    brandId?: string | number;
    // @deprecated
    categoryId?: string | number;
    categoryUid?: string;
    seller?: string;
    renderExcludedFacets?: string[];
    renderIncludedFacets?: string[];
    includedFacets?: string[];
    predefinedFilters?: DefinedFilter;
    sortFn?: ((facets: Facets) => Facets) | null;
  }>(),
  {
    type: 'default',
    value: () => new Map<string, AllFacets>(),
    search: '',
    brandId: '',
    categoryId: '',
    categoryUid: '',
    seller: '',
    renderExcludedFacets: () => [],
    renderIncludedFacets: () => [],
    includedFacets: () => [],
    predefinedFilters: () => ({}),
    sortFn: () => defaultSortFn,
  },
);

/*
 * The type of product listing to display (default , discounted or search).
 * @property type
 * @type ProductListingType
 * @public
 * @required
 * @default ProductListingType.default
 */

function disabledFacet(facet: IFacet, excluded?: string[]): boolean {
  if (!excluded?.length) {
    return false;
  }

  return excluded?.includes(facet.code);
}

const isFiltering = ref(false);
const data = ref<ListingFullData | null>(null);
const { selectedOption } = useSortBy();
const { nextPage, currentPage, pageSize, updatePageInfo } = usePagination({ pageSize: 6 });
const aggregations = computed(() => {
  return data.value?.connection?.aggregations;
});

/**
 * Same as updateFacetWithSelectedValue but for the facet dialog as it accepts a MappedAggregation[]
 */
const updateFacetDialogWithSelectedValue = (_facets: Facets) => {
  currentPage.value = 1;
  nextTick(() => (facets.value = _facets));
};

const appliedFacet = computed<Facets>(() => {
  const _facets = new Map(
    [...facets.value].filter(([, value]) => {
      return enabledFacet(value, props.renderIncludedFacets) && !disabledFacet(value, props.renderExcludedFacets);
    }),
  );

  return props.sortFn ? props.sortFn(toRaw(_facets)) : _facets;
});

const { facets, filters, clearAll, removeItem } = useAppFacets(aggregations, {
  exclude: [],
  only: props.includedFacets,
  whiteListedParams: [
    /title-by/,
    /calc/,
    /extras/,
    ...Object.keys(props.predefinedFilters).map<RegExp>(key => {
      return new RegExp(String.raw`${key}`);
    }),
  ],
});

const categories = ref([]);

const variables = computed(() => {
  return {
    page: currentPage.value,
    pageSize: pageSize.value,
    sort: merge(
      props.type === 'search' ? { relevance: SortEnum.Desc } : defaultSortOption,
      selectedOption.value?.value || defaultSortOption,
    ),
    filter: merge(
      props.categoryId === 'NAN' ? omit(filters.value, 'category_id') : filters.value,
      props.predefinedFilters,
      props.categoryId === 'NAN'
        ? {}
        : props.categoryId
          ? { category_id: { eq: props.categoryId.toString() ?? '' } }
          : {},
      props.categoryUid ? { category_uid: { eq: props.categoryUid } } : {},
    ),
    withAggregations: true,
    withPaging: true,
    search: props.search,
  };
});

const {
  products,
  totalCount,
  data: productData,
  isFetching,
} = useGenericExtendedFetchProduct(props.type ?? 'default', variables);

/**
 * controls the display of the facet tags when a filter is applied
 */
const displayFacetTags = computed(() => {
  const tags = [...appliedFacet.value].filter(([, value]) => {
    if (isPriceProps(value)) {
      return isValidPriceValue(value);
    }
    return Array.isArray(value.value) ? value.value.length : !!value.value;
  });
  return !!tags?.length;
});

const slots = defineSlots<{
  emptyReplacement(): any;
  empty(): any;
  default(): any;
  'filter-banner'(): any;
}>();
/**
 * if we dont have a replacement empty view
 * or we are currently in the filtering process
 * or we have an empty replacement view, we are not currently filtering and we have no products to display
 */
const showListingView = computed(() => {
  return (
    !slots.emptyReplacement ||
    !!displayFacetTags.value ||
    (!!slots.emptyReplacement && !displayFacetTags.value && totalCount.value > 0)
  );
});

/**
 * called on selected a facet from the facet dropdowns
 * handles updating the current facet with the new selected value
 */
const updateFacetWithSelectedValue = (
  value: AggregationOption | AggregationOption[] | number[] | null | undefined,
  facet: IFacet,
) => {
  currentPage.value = 1;
  nextTick(() => {
    if (isSelectProps(facet)) {
      facet.value = value as AggregationOption[];
      return;
    }

    if (isPriceProps(facet)) {
      facet.value = value as number[];
      return;
    }

    if (isToggleProps(facet) && isAggregationOption(value)) {
      facet.value = value;
      return;
    }

    if (isToggleProps(facet)) {
      facet.value = null;
      return;
    }
  });
};

watch(productData, value => {
  updatePageInfo({ ...value?.connection?.page_info, __typename: 'SearchResultPageInfo' });
  data.value = value;
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Facets): void;
  (e: 'categories', value: ISelectFacet): void;
  (e: 'fetching', value: boolean): void;
}>();

// emit fetching state to the parent component
watch(
  isFetching,
  value => {
    emit('fetching', value);
  },
  {
    immediate: true,
  },
);

/*
 * called on filter option change dropdown change
 * emits the new filter options to the parent component
 * @param {AggregationOption[]} value
 * */
watch(facets, value => {
  currentPage.value = 1;
  emit('update:modelValue', value);
});

watch(categories, (value: any) => {
  emit('categories', value);
});

/*
 * called on filter option change dropdown change
 * emits the new filter options to the parent component
 * @param {AggregationOption[]} value
 * */
watch(
  () => props.value,
  value => {
    if (!value) return;
    facets.value = value;
  },
);
console.log(appliedFacet, 'facet');
</script>

<script lang="ts">
const defaultFacets = [
  'product_brand_id',
  'category_uid',
  'compressor_type',
  'smart_technology',
  'cooling_system',
  'ac_horse_power',
  'price',
  'on_sale',
] as const; // to be treated as a readonly tuple.

const defaultSortFn = (facets: MaybeRef<Facets> | null) => {
  if (!facets) {
    return new Map<(typeof defaultFacets)[number], AllFacets>();
  }

  return defaultFacets.reduce<Facets>((acc, facet: (typeof defaultFacets)[number]) => {
    if (!isRef(facets) && facets.has(facet) && facets.get(facet)) {
      // assertion is needed here because we are sure that the value is not null
      acc.set(facet, facets.get(facet) as AllFacets);
    }
    return acc;
  }, new Map<(typeof defaultFacets)[number], AllFacets>());
};
export default {};
</script>

<style lang="postcss" scoped>
.ProductListing {
  &__inner {
    @apply mt-6;

    display: grid;
    grid-template-columns: 1fr;
    @screen lg {
      grid-template-columns: 330px 1fr;
      column-gap: 60px;
    }
  }
  &__filters {
    @apply bg-white px-8 py-10;
    box-shadow: 0 10px 48px -12px rgba(21, 44, 115, 0.15);
    border-radius: 20px;
  }
}

.Facets {
  div:not(:last-child) {
    @apply border-b border-tertiary-600;
  }

  div:not(:first-child) {
    @apply pt-3;
  }

  div:not(:last-child) {
    @apply pb-3;
  }
}
</style>

<i18n>
{
  "en": {
    "notFoundTitle": "We couldn't find products that match your criteria",
    "notFoundTip": "Try using different filters to find other products",
    "filterBy": "Filter by",
    "noResults": "No results",
    "filterProducts": "Filter products",
    "clearAll": "Clear all",
    "products": "no products | {totalCount} Products | {totalCount} Products",
    "noResultsFound": "No Results Found",
    "noResultsFoundDescription": "We couldn’t find products that match your criteria, Try using different filters to find other products. "
  },
  "ar": {
    "notFoundTitle": "لم يتم العثور على منتجات متطابقة",
    "notFoundTip": "من فضلك قم باختيار مرشحات مختلفة للعثور على منتجات اخري",
    "filterBy": "تسوق حسب",
    "noResults": "لا يوجد نتائج",
    "filterProducts": "فلترة المنتجات",
    "clearAll": "مسح الكل",
    "products": "لا توجد منتجات | {totalCount} منتج | {totalCount} منتج",
    "noResultsFound": "لم يتم العثور على نتائج",
    "noResultsFoundDescription": "لم يتم العثور على نتائج"
  }
}
</i18n>
